// Eskom PARCC — Bulk Import (admin only).
//
// The monolith's `processBulkUpload` flow (git 6810c83:index.html ~1430),
// rebuilt for React + Data Connect: an admin uploads an XLSX/CSV; the file is
// parsed, the header row auto-detected, columns mapped through the alias map,
// and each row turned into a transaction-upsert payload + its findings (see
// importParse.ts). The screen runs in two explicit phases:
//
//   1. PREVIEW — on file select the workbook is parsed locally (no writes). The
//      mapped rows are shown with their reference, dataset, supplier, value and
//      finding count; rows without a usable reference are flagged "skipped". The
//      admin reviews, then confirms.
//   2. IMPORT — on Confirm (admin re-checked, button disabled in flight) each
//      mappable row is upserted then its findings replaced, SEQUENTIALLY. A row
//      failing is recorded as an error and the batch CONTINUES — no silent
//      drops. Insert-vs-update is distinguished against the current register
//      cache. A results table + summary toast report every outcome; caches are
//      invalidated so the register reflects the new/updated rows.
import { useMemo, useRef, useState } from 'react';
import {
  Card,
  Button,
  DataTable,
  LoadingState,
  ErrorState,
  EmptyState,
  useToast,
  type Column,
} from '../../components/ui';
import {
  useTransactions,
  useFindings,
  useUpsertTransaction,
  useReplaceFindings,
} from '../../hooks';
import { useAuth } from '../../lib/auth';
import { adminApi } from '../../lib/adminApi';
import { recordAudit, withAuditSuppressed } from '../../lib/dbWrite';
import { formatRand } from '../../lib/money';
import type { FindingRead } from '../../lib/db';
import {
  parseToRecords,
  rowToImport,
  mergeImportFindings,
  isUnchangedForImport,
  importFindingsMatch,
  lastOccurrenceByRef,
  refKey,
  type ImportRecord,
  type ImportRow,
} from './importParse';
import { RemovalHistory } from './RemovalHistory';
import { ImportBulkTabs } from './ImportBulkTabs';

/** A previewed row: either mappable (with its payload) or skipped (no reference). */
interface PreviewRow {
  /** 1-based row number among the data rows (for the results table). */
  rowNo: number;
  record: ImportRecord;
  mapped: ImportRow | null;
}

type Outcome = 'Created' | 'Updated' | 'Revived' | 'Unchanged' | 'Duplicate' | 'Skipped' | 'Error';

interface ResultRow {
  rowNo: number;
  reference: string;
  outcome: Outcome;
  findings: number;
  message: string;
}

interface ImportSummary {
  processed: number;
  created: number;
  updated: number;
  /** Rows that resolved to a soft-deleted reference and were un-deleted (WS2). */
  revived: number;
  /** Rows identical to the register on every import column — nothing written (WS2). */
  unchanged: number;
  /** Earlier occurrences of an in-batch duplicate reference — last wins (WS2). */
  duplicates: number;
  skipped: number;
  errors: number;
  findings: number;
}

const ACCEPT = '.xlsx,.xlsm,.xls,.csv';

/**
 * The ONE import-summary sentence, used verbatim by the audit trail, the toast
 * and the results panel — so what the admin sees is exactly what the trail
 * records. Zero buckets that are usually zero (duplicates/skipped/errors) are
 * included only when non-zero; the core counts always show.
 */
function summariseImport(s: ImportSummary): string {
  const parts = [
    `${s.created} added`,
    `${s.updated} updated`,
    `${s.revived} revived`,
    `${s.unchanged} unchanged`,
  ];
  if (s.duplicates) parts.push(`${s.duplicates} duplicate(s)`);
  if (s.skipped) parts.push(`${s.skipped} skipped`);
  if (s.errors) parts.push(`${s.errors} error(s)`);
  parts.push(`${s.findings} finding line(s)`);
  return parts.join(', ');
}

export function ImportPage(): React.JSX.Element {
  const { role } = useAuth();
  const { show } = useToast();
  const transactionsQuery = useTransactions();
  const findingsQuery = useFindings();
  // Import writes REVIVE soft-deleted rows: a "replace population" purge soft-deletes
  // the old rows (keeping their @unique referenceNo), so re-uploading the same refs
  // lands as an update on a hidden row — `revive` un-deletes it so the fresh
  // population actually appears. (No-op for brand-new refs and live rows.)
  // `importSource` (WS2, 1 Jul 2026 incident) routes updates through the
  // import-scoped mapper so a re-import can never overwrite review state.
  const upsertTransaction = useUpsertTransaction({ revive: true, importSource: true });
  const replaceFindings = useReplaceFindings();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string>('');
  const [parseError, setParseError] = useState<string>('');
  const [preview, setPreview] = useState<PreviewRow[] | null>(null);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<ResultRow[] | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [purging, setPurging] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [purgeNote, setPurgeNote] = useState('');
  // Bumped after each purge so the removal-history card reloads its batch list.
  const [historyReload, setHistoryReload] = useState(0);

  const isAdmin = role === 'admin';

  // The current register, lower-cased by reference, to distinguish insert vs
  // update at preview/report time without an extra round-trip (the upsert hook
  // also re-resolves server-side; this is purely for the outcome label).
  const existingRefs = useMemo(() => {
    const set = new Set<string>();
    for (const t of transactionsQuery.data ?? []) {
      const ref = String(t.referenceNo ?? '')
        .trim()
        .toLowerCase();
      if (ref) set.add(ref);
    }
    return set;
  }, [transactionsQuery.data]);

  // (The findings-by-ref grouping for merge-preserve is built INSIDE runImport
  // from a fresh refetch — WS2 — so the merge never works off a stale cache.)

  function reset(): void {
    setFileName('');
    setParseError('');
    setPreview(null);
    setResults(null);
    setSummary(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleFile(file: File): Promise<void> {
    setParseError('');
    setResults(null);
    setSummary(null);
    setPreview(null);
    setFileName(file.name);
    try {
      const isCsv = /\.csv$/i.test(file.name);
      // SheetJS reads both: CSV as a string, XLSX/XLS as an ArrayBuffer.
      const records = isCsv
        ? parseToRecords(await file.text())
        : parseToRecords(await file.arrayBuffer());
      if (!records.length) {
        throw new Error('No usable rows were found in the selected file.');
      }
      const rows: PreviewRow[] = records.map((record, idx) => ({
        rowNo: idx + 1,
        record,
        mapped: rowToImport(record),
      }));
      setPreview(rows);
    } catch (error) {
      setPreview(null);
      const message = error instanceof Error ? error.message : 'The file could not be read.';
      setParseError(message);
      show('Could not read the file. See the message on the page.', 'error');
    }
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  }

  async function runImport(): Promise<void> {
    // Defence-in-depth: re-confirm the admin role in the write handler, not only
    // at the route gate.
    if (!isAdmin) {
      show('Only administrators may import transactions.', 'error');
      return;
    }
    if (!preview) return;

    setRunning(true);

    // WS2 (1 Jul 2026 incident): refresh both caches ONCE up front so the
    // skip-unchanged diff and the findings merge-preserve work from current data,
    // not a stale page cache. A refetch failure falls back to the cached data for
    // classification/merging — but DISABLES skip-unchanged (freshOk): a stale
    // cache can show a just-purged population as live-and-identical, and skipping
    // those rows would skip their revive (an import that silently imports
    // nothing). Writes are always safe against stale data; only the decision NOT
    // to write needs fresh truth.
    const fresh = <T,>(q: { refetch: () => unknown; data?: T }): Promise<T | undefined> =>
      Promise.resolve(q.refetch()).then(
        (r) => (r as { data?: T } | undefined)?.data,
        () => undefined,
      );
    const [txFresh, findingsFresh] = await Promise.all([
      fresh(transactionsQuery),
      fresh(findingsQuery),
    ]);
    const freshOk = txFresh !== undefined && findingsFresh !== undefined;
    const txRows = txFresh ?? transactionsQuery.data ?? [];
    const findingRows = (findingsFresh ?? findingsQuery.data ?? []) as FindingRead[];
    const liveByRef = new Map<string, (typeof txRows)[number]>();
    for (const t of txRows) {
      const ref = refKey(t.referenceNo);
      if (ref) liveByRef.set(ref, t);
    }
    const freshFindingsByRef = new Map<string, FindingRead[]>();
    for (const f of findingRows) {
      const ref = refKey(f.referenceNo);
      if (!ref) continue;
      const bucket = freshFindingsByRef.get(ref);
      if (bucket) bucket.push(f);
      else freshFindingsByRef.set(ref, [f]);
    }
    // In-batch duplicate references resolve LAST-WINS (the appended row is the
    // correction); earlier occurrences are reported, never written — which also
    // stops a second write's findings-merge from working off a stale snapshot
    // and silently deleting the first occurrence's just-written findings.
    const lastByRef = lastOccurrenceByRef(
      preview.map((p) => p.mapped?.transaction.referenceNo ?? null),
    );

    const out: ResultRow[] = [];
    const s: ImportSummary = {
      processed: preview.length,
      created: 0,
      updated: 0,
      revived: 0,
      unchanged: 0,
      duplicates: 0,
      skipped: 0,
      errors: 0,
      findings: 0,
    };

    // Sequential: each row is independent, and a failure must be isolated (the
    // batch continues) rather than aborting. A per-row try/catch collects errors.
    // The per-row transaction/finding writes would each emit an audit entry;
    // suppress them and record ONE `import_population` summary below, so a 2000-row
    // import is one accountable line in the trail, not 2000.
    await withAuditSuppressed(async () => {
      for (const [index, row] of preview.entries()) {
        if (!row.mapped) {
          s.skipped += 1;
          out.push({
            rowNo: row.rowNo,
            reference: '',
            outcome: 'Skipped',
            findings: 0,
            message: 'No usable reference number found in this row.',
          });
          continue;
        }
        const { transaction, findings } = row.mapped;
        const ref = transaction.referenceNo;
        const key = refKey(ref);
        if (lastByRef.get(key) !== index) {
          s.duplicates += 1;
          out.push({
            rowNo: row.rowNo,
            reference: ref,
            outcome: 'Duplicate',
            findings: 0,
            message:
              'Duplicate reference in this file — a later row supersedes this one (the last occurrence wins).',
          });
          continue;
        }
        const existing = liveByRef.get(key);
        // Skip-unchanged needs ALL of: a successful refresh (freshOk — never
        // decide "nothing to do" from a stale cache), a case-exact reference match
        // (the write path resolves case-sensitively; a case-mismatched "match"
        // would skip a row the server sees as different), an identical row on
        // every diffed column, AND import-sourced findings already matching the
        // file's lines (so a findings-only change, or a row whose earlier findings
        // replace failed, still lands on the natural re-run).
        if (
          freshOk &&
          existing &&
          existing.referenceNo.trim() === ref.trim() &&
          isUnchangedForImport(existing, transaction) &&
          importFindingsMatch(freshFindingsByRef.get(key) ?? [], findings)
        ) {
          s.unchanged += 1;
          out.push({
            rowNo: row.rowNo,
            reference: ref,
            outcome: 'Unchanged',
            findings: 0,
            message: 'Identical to the current register row — nothing written.',
          });
          continue;
        }
        try {
          // No audit context: the whole import runs inside withAuditSuppressed and
          // must not write a per-row diff for a 2000-row population (one summary
          // row stands for the run).
          const upserted = await upsertTransaction.mutateAsync({ input: transaction });
          // Refresh the imported transaction's findings WITHOUT destroying
          // analyst/CES-captured work (the monolith's upsert-by-source behaviour):
          // merge the row's CURRENT findings (from the fresh read, grouped by ref)
          // with the new import lines so that only the import-sourced findings are
          // superseded, then hand the union to replaceFindings. A row with NO
          // import findings is left untouched entirely (no replace).
          if (findings.length) {
            const existingForRef = freshFindingsByRef.get(key) ?? [];
            const merged = mergeImportFindings(existingForRef, findings);
            await replaceFindings.mutateAsync({ referenceNo: ref, findings: merged });
          }
          // Honest outcome from the branch the WRITE actually took: an update on a
          // reference absent from the LIVE register means the row was soft-deleted
          // and has been revived — before WS2 these mis-reported as "added" (the
          // 15:24 "57 added" line in the incident trail was 47 revives).
          const outcome: Outcome =
            upserted.action === 'inserted' ? 'Created' : existing ? 'Updated' : 'Revived';
          if (outcome === 'Created') s.created += 1;
          else if (outcome === 'Updated') s.updated += 1;
          else s.revived += 1;
          s.findings += findings.length;
          const verb =
            outcome === 'Created'
              ? 'Imported new'
              : outcome === 'Updated'
                ? 'Updated existing'
                : 'Revived previously removed';
          out.push({
            rowNo: row.rowNo,
            reference: ref,
            outcome,
            findings: findings.length,
            message: `${verb} reference${
              findings.length ? ` with ${findings.length} finding line(s)` : ''
            }.`,
          });
        } catch (error) {
          s.errors += 1;
          out.push({
            rowNo: row.rowNo,
            reference: ref,
            outcome: 'Error',
            findings: 0,
            message: error instanceof Error ? error.message : String(error),
          });
        }
      }
    });

    // ONE summary audit for the whole import (server-stamped actor), telling the
    // SAME story as the toast + results panel (summariseImport).
    await recordAudit({
      action: 'import_population',
      entityType: 'Transaction',
      entityId: 'population',
      note: summariseImport(s),
    });

    setResults(out);
    setSummary(s);
    setRunning(false);
    show(`Import complete: ${summariseImport(s)}.`, s.errors ? 'error' : 'success');
    // The mutation hooks invalidate the transactions + findings caches on each
    // success, so the register reflects the import without an explicit refetch.
  }

  /** Remove the current population (reversible soft-delete) via the admin service. */
  async function handlePurge(): Promise<void> {
    if (!isAdmin) {
      show('Only administrators may remove the population.', 'error');
      return;
    }
    if (purgeConfirm.trim() !== 'DELETE') {
      show('Type DELETE to confirm removing the current population.', 'error');
      return;
    }
    setPurging(true);
    try {
      const r = await adminApi.purgePopulation(purgeNote);
      show(
        `Removed ${r.transactions.toLocaleString('en-ZA')} transaction(s) and ${r.findings.toLocaleString('en-ZA')} finding(s) as a restorable batch. Upload the new population above.`,
        'success',
      );
      setPurgeConfirm('');
      setPurgeNote('');
      setHistoryReload((k) => k + 1);
      void transactionsQuery.refetch();
      void findingsQuery.refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : 'Failed to remove the population.', 'error');
    } finally {
      setPurging(false);
    }
  }

  const mappableCount = useMemo(
    () => (preview ? preview.filter((r) => r.mapped).length : 0),
    [preview],
  );
  const skippableCount = (preview?.length ?? 0) - mappableCount;

  // --- Loading / error of the register read (needed for insert-vs-update) ---
  if (transactionsQuery.isLoading) return <LoadingState message="Loading the register…" />;
  if (transactionsQuery.error) {
    const err = transactionsQuery.error;
    return (
      <ErrorState
        message="Could not load the register; an import needs it to detect new vs existing references."
        detail={err instanceof Error ? err.message : String(err)}
        onRetry={() => {
          void transactionsQuery.refetch();
        }}
      />
    );
  }

  const previewColumns: Column<PreviewRow>[] = [
    { key: 'rowNo', header: '#', accessor: (r) => r.rowNo, render: (r) => r.rowNo },
    {
      key: 'reference',
      header: 'Reference No',
      accessor: (r) => r.mapped?.transaction.referenceNo ?? '',
      render: (r) => r.mapped?.transaction.referenceNo || <span className="small">—</span>,
    },
    {
      key: 'dataset',
      header: 'Dataset',
      accessor: (r) => r.mapped?.transaction.dataset ?? '',
      render: (r) => r.mapped?.transaction.dataset ?? '—',
    },
    {
      key: 'supplier',
      header: 'Supplier',
      accessor: (r) => r.mapped?.transaction.supplier ?? '',
      render: (r) => r.mapped?.transaction.supplier || '—',
    },
    {
      key: 'value',
      header: 'Value',
      align: 'right',
      accessor: (r) => r.mapped?.transaction.value ?? 0,
      render: (r) => (r.mapped ? formatRand(r.mapped.transaction.value ?? 0) : '—'),
    },
    {
      key: 'findings',
      header: 'Findings',
      align: 'right',
      accessor: (r) => r.mapped?.findings.length ?? 0,
      render: (r) => r.mapped?.findings.length ?? 0,
    },
    {
      key: 'outcome',
      header: 'Status',
      accessor: (r) => (r.mapped ? 'Will import' : 'Skip'),
      render: (r) =>
        r.mapped ? (
          <span className="badge">
            {existingRefs.has(r.mapped.transaction.referenceNo.trim().toLowerCase())
              ? 'Will update'
              : 'Will add'}
          </span>
        ) : (
          <span className="badge" title="No usable reference number">
            Skip — no reference
          </span>
        ),
    },
  ];

  const resultColumns: Column<ResultRow>[] = [
    { key: 'rowNo', header: '#', accessor: (r) => r.rowNo, render: (r) => r.rowNo },
    {
      key: 'reference',
      header: 'Reference No',
      accessor: (r) => r.reference,
      render: (r) => r.reference || '—',
    },
    {
      key: 'outcome',
      header: 'Outcome',
      accessor: (r) => r.outcome,
      render: (r) => <span className="badge">{r.outcome}</span>,
    },
    {
      key: 'findings',
      header: 'Findings',
      align: 'right',
      accessor: (r) => r.findings,
      render: (r) => r.findings,
    },
    { key: 'message', header: 'Detail', accessor: (r) => r.message, render: (r) => r.message },
  ];

  return (
    <>
      <ImportBulkTabs />
      <Card>
        <div className="section-head">
          <div>
            <h3>Import transactions</h3>
            <div className="small">
              Upload an Excel (.xlsx/.xls) or CSV file. The header row is detected automatically and
              columns are mapped by name. Review the preview, then import.
            </div>
          </div>
          <div className="actions">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              onChange={onFileChange}
              disabled={running}
              aria-label="Choose a CSV or Excel file to import"
              data-testid="import-file"
            />
            {(preview || results || parseError) && (
              <Button variant="ghost" onClick={reset} disabled={running}>
                Clear
              </Button>
            )}
          </div>
        </div>

        {!isAdmin && <ErrorState message="Only administrators may import transactions." />}

        {fileName && <div className="small">Selected: {fileName}</div>}

        {parseError && <ErrorState message="The file could not be imported." detail={parseError} />}
      </Card>

      {preview && !results && (
        <Card>
          <div className="section-head">
            <div>
              <h3>Preview</h3>
              <div className="small">
                {mappableCount.toLocaleString('en-ZA')} row(s) ready to import
                {skippableCount
                  ? `, ${skippableCount.toLocaleString('en-ZA')} will be skipped (no reference)`
                  : ''}
                .
              </div>
            </div>
            <div className="actions">
              <Button
                variant="primary"
                onClick={() => void runImport()}
                disabled={!isAdmin || running || mappableCount === 0}
                data-testid="import-confirm"
              >
                {running ? 'Importing…' : `Import ${mappableCount.toLocaleString('en-ZA')} row(s)`}
              </Button>
            </div>
          </div>
          {preview.length === 0 ? (
            <EmptyState message="No rows to preview." />
          ) : (
            <DataTable
              columns={previewColumns}
              rows={preview}
              getRowKey={(r) => String(r.rowNo)}
              emptyMessage="No rows to preview."
            />
          )}
        </Card>
      )}

      {summary && results && (
        <Card>
          <div className="section-head">
            <div>
              <h3>Import results</h3>
              <div className="small">
                {summary.processed.toLocaleString('en-ZA')} row(s) processed —{' '}
                {summariseImport(summary)}.
              </div>
            </div>
            <div className="actions">
              <Button variant="ghost" onClick={reset}>
                Import another file
              </Button>
            </div>
          </div>
          <DataTable
            columns={resultColumns}
            rows={results}
            getRowKey={(r) => String(r.rowNo)}
            emptyMessage="No results."
          />
        </Card>
      )}

      {isAdmin && (
        <Card>
          <div className="section-head">
            <div>
              <h3>Danger zone — replace the population</h3>
              <div className="small">
                Remove the entire current population, then upload the new file above to make it the
                live register. This is a <strong>reversible soft-delete</strong> — the removal is
                recorded as a batch in the removal history below, and any batch can be restored.
                Reviewers&apos; captured working papers for removed transactions are hidden, not
                destroyed.
              </div>
            </div>
          </div>
          <div className="actions" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              <span className="small">Note (optional — shown in the removal history)</span>
              <input
                value={purgeNote}
                onChange={(e) => setPurgeNote(e.target.value)}
                placeholder="e.g. Replacing with the Q2 population"
                disabled={purging}
                aria-label="Optional note for this removal"
                data-testid="purge-note"
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              <span className="small">Type DELETE to confirm</span>
              <input
                value={purgeConfirm}
                onChange={(e) => setPurgeConfirm(e.target.value)}
                placeholder="DELETE"
                disabled={purging}
                aria-label="Type DELETE to confirm removing the population"
              />
            </label>
            <Button
              variant="primary"
              onClick={() => void handlePurge()}
              disabled={purging || purgeConfirm.trim() !== 'DELETE'}
            >
              {purging ? 'Working…' : 'Remove current population'}
            </Button>
          </div>
        </Card>
      )}

      {isAdmin && (
        <RemovalHistory
          reloadKey={historyReload}
          onRestored={() => {
            void transactionsQuery.refetch();
            void findingsQuery.refetch();
          }}
        />
      )}
    </>
  );
}
